import React, { useState } from 'react';
import { useApi } from '../../Api';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { 
  IncomeStatementProps, 
  IncomeStatementResponse, 
  IncomeStatementRequest 
} from '../types/IncomeStatement';
import { DIMENSION_TYPE_OPTIONS, DimensionType, INCOME_STATEMENT_TYPE_OPTIONS, ReportType } from '../../constants/accounting';
import { FormSelect } from '../../controls/FormSelect';
import CustomInput from '../../controls/CustomInput';

const IncomeStatement: React.FC<IncomeStatementProps> = ({ familyId, locationId }) => {
  const api = useApi();
  const [incomeStatement, setIncomeStatement] = useState<IncomeStatementResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noData, setNoData] = useState(false);
  const [request, setRequest] = useState<IncomeStatementRequest>({
    reportEndDate: new Date().toISOString().split('T')[0],
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // First day of current month
    type: 'unconsolidated',
    journalStatus: 'all'
  });

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const toggleSection = (sectionLabel: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionLabel)) {
      newExpanded.delete(sectionLabel);
    } else {
      newExpanded.add(sectionLabel);
    }
    setExpandedSections(newExpanded);
  };

  const isSectionExpanded = (sectionLabel: string): boolean => {
    return expandedSections.has(sectionLabel);
  };

  const fetchIncomeStatement = async () => {
    setLoading(true);
    setError(null);
    setNoData(false);
    
    try {
      const response = await api.get('/api/accounting/IncomeStatement', {
        params: {
          familyId: familyId,
          reportEndDate: request.reportEndDate,
          startDate: request.startDate,
          type: request.type,
          journalStatus: request.journalStatus,
          ...(request.comparativeColumnType && { comparativeColumnType: request.comparativeColumnType }),
          ...(request.dimensionType && { dimensionType: request.dimensionType }),
          ...(request.dimensionIds && { dimensionIds: request.dimensionIds.join(',') }),
          ...(locationId && { locationId: locationId })
        },
      });
      
      // The backend returns an IncomeStatementResponse object with an IncomeStatement property
      if (response.data && response.data.incomeStatement) {
        setIncomeStatement(response.data.incomeStatement);
        setError(null); // Clear any previous errors
        setNoData(false);
      } else {
        // No data is not an error - just set empty state
        setIncomeStatement(null);
        setError(null);
        setNoData(true);
      }
    } catch (err: any) {
      console.error('Error fetching income statement:', err);
      setError(err.response?.data?.message || 'An error occurred while fetching the income statement');
      setIncomeStatement(null);
    } finally {
      setLoading(false);
    }
  };

  const validateDates = (): string | null => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today
    
    const reportEndDate = new Date(request.reportEndDate);
    if (reportEndDate > today) {
      return 'Report end date cannot be in the future';
    }
    
    const startDate = new Date(request.startDate);
    if (startDate > today) {
      return 'Start date cannot be in the future';
    }
    
    if (startDate > reportEndDate) {
      return 'Start date cannot be after the report end date';
    }
    
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const dateError = validateDates();
    if (dateError) {
      setError(dateError);
      return;
    }
    
    fetchIncomeStatement();
  };

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const getValueColor = (value: string): string => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'text-gray-800';
    if (num > 0) return 'text-green-600';
    if (num < 0) return 'text-red-600';
    return 'text-gray-800';
  };

  const getSectionTotal = (sectionLabel: string): number => {
    const section = incomeStatement?.rows.find(row => 
      row.rowType === 'summary' && 
      row.label === `Total ${sectionLabel}`
    );
    
    if (section && section.rowType === 'summary') {
      return section.value;
    }
    return 0;
  };

  const renderRow = (row: any, index: number) => {
    if (row.rowType === 'header') {
      // Main section headers (REVENUE, EXPENSES, NET INCOME)
      if (row.depth === 0) {
        return (
          <div key={index} className="font-bold text-xl text-gray-900 py-4 px-4 bg-gray-100 border-b border-gray-300 my-3 rounded">
            {row.label}
          </div>
        );
      }
      
      // Sub-section headers
      if (row.depth === 1) {
        const sectionTotal = getSectionTotal(row.label);
        
        return (
          <div key={index} className="border-b border-gray-200">
            <div className="flex justify-between items-center py-3 px-4 bg-white">
              <span className="font-semibold text-base text-gray-800">{row.label}</span>
              <span className={`font-semibold text-base ${getValueColor(sectionTotal.toString())}`}>
                {formatCurrency(sectionTotal.toString())}
              </span>
            </div>
          </div>
        );
      }
      
      // Account group headers
      if (row.depth >= 2) {
        const sectionTotal = getSectionTotal(row.label);
        const expanded = isSectionExpanded(row.label);
        
        const hasAccounts = incomeStatement?.rows.some((r, rIndex) => {
          if (rIndex > index && 
              r.rowType === 'accounts' && 
              r.accountBalances && 
              r.accountBalances.length > 0 &&
              r.depth === row.depth + 1) {
            
            const hasIntermediateHeaders = incomeStatement.rows.slice(index + 1, rIndex).some(betweenRow => 
              betweenRow.rowType === 'header'
            );
            
            return !hasIntermediateHeaders;
          }
          return false;
        });
        
        if (hasAccounts) {
          return (
            <div key={index} className="border-b border-gray-100">
              <button
                onClick={() => toggleSection(row.label)}
                className="w-full flex justify-between items-center py-2 px-6 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex items-baseline space-x-2">
                  <span className={`transform transition-transform duration-200 text-gray-400 text-2xl leading-none ${expanded ? 'rotate-90' : ''}`}>
                    ›
                  </span>
                  <span className="font-medium text-sm text-gray-700">{row.label}</span>
                </div>
                <span className={`font-medium text-sm ${getValueColor(sectionTotal.toString())}`}>
                  {formatCurrency(sectionTotal.toString())}
                </span>
              </button>
            </div>
          );
        } else {
          return (
            <div key={index} className="border-b border-gray-100">
              <div className="flex justify-between items-center py-2 px-6 bg-white">
                <span className="font-medium text-sm text-gray-700">{row.label}</span>
                <span className={`font-medium text-sm ${getValueColor(sectionTotal.toString())}`}>
                  {formatCurrency(sectionTotal.toString())}
                </span>
              </div>
            </div>
          );
        }
      }
      
      return (
        <div key={index} className="font-medium text-sm text-gray-600 py-2 px-8">
          {row.label}
        </div>
      );
    }
    
    if (row.rowType === 'accounts') {
      if (row.accountBalances && row.accountBalances.length > 0) {
        return (
          <div key={index} className="space-y-1">
            {row.accountBalances && row.accountBalances.length > 0 ? (
              (() => {
                let parentSectionLabel = '';
                for (let i = index - 1; i >= 0; i--) {
                  const prevRow = incomeStatement?.rows[i];
                  if (prevRow && prevRow.rowType === 'header' && prevRow.depth >= 1) {
                    parentSectionLabel = prevRow.label;
                    break;
                  }
                }
                
                if (parentSectionLabel && isSectionExpanded(parentSectionLabel)) {
                  return row.accountBalances.map((account: any, accIndex: number) => (
                    <div key={accIndex} className="flex justify-between items-center py-1 px-8 text-sm bg-white">
                      <span className="text-gray-700">
                        {account.accountNumber} - {account.accountName}
                      </span>
                      <span className={`font-mono ${getValueColor(account.balance.toString())}`}>
                        {formatCurrency(account.balance.toString())}
                      </span>
                    </div>
                  ));
                } else {
                  return null;
                }
              })()
            ) : (
              <div className="py-1 px-8 text-sm text-gray-500">
                No account details available
              </div>
            )}
          </div>
        );
      }
      
      return (
        <div key={index} className="py-1 px-8 text-sm text-gray-500">
          No account details available
        </div>
      );
    }
    
    if (row.rowType === 'calculated') {
      if (!row.value) {
        return (
          <div key={index} className="py-2 px-6">
            <span className="text-gray-500">No value available</span>
          </div>
        );
      }
      
      const value = row.value;
      const displayValue = row.invertBalance ? 
        formatCurrency((value * -1).toString()) : 
        formatCurrency(value.toString());
      
      return (
        <div key={index} className="flex justify-between items-center py-2 px-6 bg-blue-50 border-t border-blue-200">
          <span className="font-medium text-blue-800">{row.label}</span>
          <span className="font-bold text-blue-800">{displayValue}</span>
        </div>
      );
    }
    
    if (row.rowType === 'summary') {
      return null;
    }
    
    return (
      <div key={index} className="py-2 px-4 text-gray-500">
        Unknown row type: {row.rowType}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-50 rounded-lg p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Row: Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CustomInput
              type="date"
              name="startDate"
              label="Start Date"
              value={request.startDate}
              onChange={(val) => setRequest({ ...request, startDate: val })}
              required
            />

            <CustomInput
              type="date"
              name="reportEndDate"
              label="End Date"
              value={request.reportEndDate}
              onChange={(val) => setRequest({ ...request, reportEndDate: val })}
              required
            />
          </div>

          {/* Second Row: Report Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormSelect
              label="Type"
              value={request.type}
              options={INCOME_STATEMENT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(val) => setRequest({ ...request, type: val as ReportType })}
              isSearchable={false}
            />

            <FormSelect
              label="Journal Status"
              value={request.journalStatus}
              options={[
                { value: 'all', label: 'All' },
                { value: 'draft', label: 'Draft' },
                { value: 'posted', label: 'Posted' },
              ]}
              onChange={(val) => setRequest({ ...request, journalStatus: val as any })}
              isSearchable={false}
            />

            <FormSelect
              label="Comparative Type"
              value={request.comparativeColumnType || ''}
              options={[
                { value: '', label: 'None' },
                { value: 'month', label: 'Month' },
                { value: 'quarter', label: 'Quarter' },
                { value: 'year', label: 'Year' },
              ]}
              onChange={(val) => setRequest({ ...request, comparativeColumnType: (val as 'month' | 'quarter' | 'year') || undefined })}
              isSearchable={false}
            />
          </div>

          {/* Third Row: Dimension Options + Button */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormSelect
              label="Dimension Type"
              value={request.dimensionType || ''}
              options={[
                { value: '', label: 'None' },
                ...DIMENSION_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
              ]}
              onChange={(val) => setRequest({
                ...request,
                dimensionType: (val as DimensionType) || undefined,
                dimensionIds: undefined,
              })}
              isSearchable={false}
            />

            <div>
              <CustomInput
                type="text"
                name="dimensionIds"
                label="Dimension IDs"
                placeholder="e.g., 1,2,3"
                value={request.dimensionIds?.join(',') || ''}
                onChange={(val) => setRequest({
                  ...request,
                  dimensionIds: val ? val.split(',').map(id => id.trim()).filter(id => id) : undefined,
                })}
                disabled={!request.dimensionType}
              />
              {request.dimensionType && (
                <p className="text-xs text-gray-500 mt-1">
                  Comma-separated Dimension._id values
                </p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center h-[calc(3.5rem+2px)]"
              >
                {loading ? 'Loading...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Content */}
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {noData && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-800">No income statement data available for the selected criteria.</p>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading income statement...</span>
          </div>
        )}

        {!loading && incomeStatement && (
          <div className="space-y-4">
            <div className="space-y-2">
              {incomeStatement.rows && Array.isArray(incomeStatement.rows) ? (
                incomeStatement.rows.map((row, index) => renderRow(row, index))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <p>No data available in the income statement response.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && !incomeStatement && !error && !noData && (
          <div className="space-y-4">
            <div className="text-center py-12 text-gray-500">
              <div className="mb-4">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to Generate Income Statement</h3>
              <p className="text-gray-600">Configure your report parameters above and click "Generate Report" to view your income statement (Profit & Loss).</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomeStatement;
